import { Alert, TextField } from '@mui/material';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from './Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { Tenant } from '../../core/types/data-models';

interface NewCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the newly created tenant after a successful POST. */
  onCreated?: (tenant: Tenant) => void;
}

/**
 * Lightweight tenant-creation dialog used by the header switcher's
 * "+ New Customer" item. Just a single name field; full management
 * (rename, delete, etc.) lives on /tenants.
 */
export function NewCustomerDialog({ open, onClose, onCreated }: NewCustomerDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (payload: Partial<Tenant>) => resources.tenants.create(payload),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      onCreated?.(tenant);
      setName('');
      onClose();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      await create.mutateAsync({ name: name.trim() });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <FormDialog
      open={open}
      title="New customer"
      submitLabel="Create"
      loading={create.isPending}
      onClose={() => {
        setName('');
        setError(null);
        onClose();
      }}
      onSubmit={handleSubmit}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Customer name"
        required
        autoFocus
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        helperText="Display name for the tenant. You can rename it later from the Customers page."
      />
    </FormDialog>
  );
}
